import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { FlightTakeoff, Refresh } from '@mui/icons-material';
import { SalaryPlaygroundResponse } from '../types/lineup';
import { loadSalaryData, readSalaryCache, toEngineInputs } from '../utils/salaryData';
import TeamLogo from '../components/TeamLogo';
import {
  BasePlayer,
  Player,
  POOL,
  SalaryState,
  TradeDraft,
  createSalaryEngine,
  fmt,
  freshState,
  round2,
} from '../utils/salaryEngine';

// Bump when the persisted state shape changes meaning.
const STATE_KEY = 'salary_playground_state_v1';

function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error loading ${key} from localStorage:`, error);
      return initialValue;
    }
  });
  const setValue = (value: T | ((prev: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error saving ${key} to localStorage:`, error);
    }
  };
  return [storedValue, setValue];
}

const cr = (x: number) => `₹${fmt(x)} Cr`;

const SalaryPlayground: React.FC = () => {
  const theme = useTheme();
  const colors = {
    ink: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    line: theme.palette.divider,
    paper: theme.palette.background.default,
    panel: theme.palette.background.paper,
    turf: theme.palette.success.main,
    turfSoft: alpha(theme.palette.success.main, 0.12),
    warn: theme.palette.warning.main,
    warnSoft: alpha(theme.palette.warning.main, 0.14),
    over: theme.palette.error.main,
    seg: theme.palette.secondary.main,
    segAlt: theme.palette.secondary.light,
  };

  // ---------- data ----------
  const [data, setData] = useState<SalaryPlaygroundResponse | null>(() => readSalaryCache());
  const [loading, setLoading] = useState(!data);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const loadData = async (force: boolean) => {
    try {
      if (force) setRefreshing(true);
      else setLoading(true);
      setError('');
      const res = await loadSalaryData(force);
      setData(res);
      if (force) setToast('Salaries refreshed from BigQuery');
    } catch (e: any) {
      setError(e?.message || 'Failed to load salary data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Guard against React StrictMode's double effect run in development.
  const fetchedOnce = useRef(false);
  useEffect(() => {
    if (!data && !fetchedOnce.current) {
      fetchedOnce.current = true;
      loadData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputs = useMemo(() => (data ? toEngineInputs(data) : null), [data]);
  const ref = inputs?.ref ?? null;
  const base = useMemo<Record<string, BasePlayer>>(() => inputs?.base ?? {}, [inputs]);

  const engine = useMemo(() => (ref ? createSalaryEngine(base, ref) : null), [base, ref]);

  // ---------- persisted state ----------
  const [stored, setStored] = useLocalStorage<SalaryState | null>(STATE_KEY, null);
  const S: SalaryState | null = useMemo(() => {
    if (!ref) return null;
    const f = freshState(ref);
    if (!stored || !stored.assign) return f;
    const merged: SalaryState = { ...f, ...stored };
    if (!ref.teams[merged.view]) merged.view = ref.codes[0];
    return merged;
  }, [ref, stored]);
  const setS = (next: SalaryState) => setStored(next);

  // ---------- transient ui state ----------
  const [hoverInfo, setHoverInfo] = useState(
    'Each block is one contract, largest first. Hover or tap a block to see who it is.'
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [q, setQ] = useState('');
  const [fTeam, setFTeam] = useState('all');
  const [fFit, setFFit] = useState<'all' | 'fit'>('all');
  const [fNat, setFNat] = useState<'all' | 'in' | 'os'>('all');
  const [sort, setSort] = useState<'pd' | 'pa' | 'n'>('pd');
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [T, setT] = useState<TradeDraft>({ a: '', b: '', sel: {} });
  const [qa, setQa] = useState('');
  const [qb, setQb] = useState('');

  // The trade builder follows the open club and always has two different clubs.
  useEffect(() => {
    if (!ref || !S) return;
    setT((prev) => {
      let a = prev.a && ref.teams[prev.a] ? prev.a : S.view;
      let b = prev.b && ref.teams[prev.b] && prev.b !== a ? prev.b : ref.codes.find((c) => c !== a) || a;
      if (a === prev.a && b === prev.b) return prev;
      return { a, b, sel: prev.sel };
    });
  }, [ref, S]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading salaries from the data warehouse…
        </Typography>
      </Container>
    );
  }
  if (error || !data || !ref || !engine || !S) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Salary data unavailable.'}</Alert>
      </Container>
    );
  }

  const E = engine;
  const t = S.view;
  const tm = ref.teams[t];
  const squad = E.squadOf(S, t).sort((a, b) => E.curPrice(S, b) - E.curPrice(S, a));
  const pay = E.payroll(S, t);
  const rem = round2(S.cap - pay);
  const osN = squad.filter((p) => E.isOS(S, p)).length;
  const cd = E.capDiff(S, t);
  const ri = E.retInfo(S, t);
  const scale = Math.max(S.cap, pay) * 1.02;
  const top3 = squad.slice(0, 3).reduce((a, p) => a + E.curPrice(S, p), 0);

  const show = (msg: string) => setToast(msg);

  // ---------- actions ----------
  const openTeam = (code: string) => {
    setS({ ...S, view: code });
    setT((prev) => {
      if (prev.a === code) return prev;
      const b = prev.b === code ? prev.a : prev.b;
      return { a: code, b, sel: {} };
    });
  };
  const release = (p: Player) => {
    if (p.custom && E.origTeam(p) === null) {
      setS(E.removeCustom(S, p.id));
      show(`Removed ${p.name}`);
    } else {
      const f = E.freedBy(S, p, t);
      setS(E.moveTo(S, p.id, POOL));
      show(`Released ${p.name}, ${cr(f)} freed`);
    }
  };
  const add = (p: Player) => {
    const from = E.curTeam(S, p);
    setS(E.moveTo(S, p.id, t));
    const r = E.retOf(p);
    show(
      from === POOL
        ? `Signed ${p.name}`
        : `Acquired ${p.name} from ${from}` + (r && from === r[0] ? '. He is no longer a retention.' : '')
    );
  };
  const startEdit = (p: Player) => {
    setEditingId(p.id);
    setEditValue(fmt(E.curPrice(S, p)));
  };
  const commitEdit = (ok: boolean) => {
    if (editingId === null) return;
    const id = editingId;
    setEditingId(null);
    const v = parseFloat(editValue);
    if (ok && !isNaN(v) && v >= 0) setS(E.setPrice(S, id, round2(v)));
  };
  const signCustom = (e: React.FormEvent) => {
    e.preventDefault();
    const name = customName.trim();
    const price = parseFloat(customPrice);
    if (!name || isNaN(price) || price < 0) return;
    setS(E.addCustom(S, name, price));
    setCustomName('');
    setCustomPrice('');
    show(`Signed ${name} for ${cr(price)}`);
  };
  const setSetting = (key: 'cap' | 'maxSquad' | 'maxOS', raw: string) => {
    const v = parseFloat(raw);
    const min = key === 'cap' ? 1 : 0;
    if (!isNaN(v) && v >= min) setS({ ...S, [key]: key === 'cap' ? round2(v) : Math.round(v) });
  };
  const resetTeam = () => {
    if (
      !window.confirm(
        `Reset ${t} to the ${data.season} salaries? Players traded in or out of ${t} go back to their original clubs.`
      )
    )
      return;
    setS(E.resetTeam(S, t));
    setT((prev) => ({ ...prev, sel: {} }));
    show(`${t} reset`);
  };
  const resetAll = () => {
    if (!window.confirm('Reset all ten clubs, the cap and squad limits?')) return;
    setS(E.resetAll(S));
    setT((prev) => ({ ...prev, sel: {} }));
    show('Everything reset');
  };

  // ---------- pool ----------
  let pool = E.allPlayers(S).filter((p) => E.curTeam(S, p) !== t);
  const ql = q.trim().toLowerCase();
  if (ql) pool = pool.filter((p) => p.name.toLowerCase().includes(ql));
  if (fTeam !== 'all') pool = pool.filter((p) => E.curTeam(S, p) === fTeam);
  if (fFit === 'fit') pool = pool.filter((p) => E.curPrice(S, p) <= rem + 1e-9);
  if (fNat === 'in') pool = pool.filter((p) => !E.isOS(S, p));
  else if (fNat === 'os') pool = pool.filter((p) => E.isOS(S, p));
  pool.sort(
    sort === 'n'
      ? (a, b) => a.name.localeCompare(b.name)
      : sort === 'pa'
      ? (a, b) => E.curPrice(S, a) - E.curPrice(S, b) || a.name.localeCompare(b.name)
      : (a, b) => E.curPrice(S, b) - E.curPrice(S, a) || a.name.localeCompare(b.name)
  );

  // ---------- trades ----------
  const items = E.tradeItems(S, T);
  const sim = items.length && T.a !== T.b ? E.simulateTrade(S, T, items) : null;
  const overClubs = sim ? [T.a, T.b].filter((c) => sim.after[c].pay > S.cap + 1e-9) : [];
  const retLost = items.filter((x) => E.isRetained(S, x.p) && E.retOf(x.p)![0] === x.from).map((x) => x.p.name);
  const tradeWarn = [
    T.a === T.b ? 'Pick two different clubs.' : '',
    overClubs.length ? `${overClubs.join(' and ')} would be over the cap.` : '',
    retLost.length ? `${retLost.join(', ')} will stop counting as a retention.` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const tradeList = (side: 'a' | 'b') => {
    const team = T[side];
    const qq = (side === 'a' ? qa : qb).toLowerCase();
    return E.squadOf(S, team)
      .filter((p) => !qq || p.name.toLowerCase().includes(qq))
      .sort((x, y) => E.curPrice(S, y) - E.curPrice(S, x));
  };
  const toggleTradeSel = (p: Player, on: boolean) =>
    setT((prev) => {
      const sel = { ...prev.sel };
      if (on) sel[p.id] = E.curPrice(S, p);
      else delete sel[p.id];
      return { ...prev, sel };
    });
  const completeTrade = () => {
    const res = E.completeTrade(S, T);
    if (!res) return;
    setS(res.S);
    setT((prev) => ({ ...prev, sel: {} }));
    show('Trade completed');
  };
  const undoTrade = () => {
    setS(E.undoTrade(S));
    show('Trade undone');
  };

  // ---------- moves + league ----------
  const mv = E.movesFor(S, t);
  const leagueRows = ref.codes
    .map((c) => ({ c, pay: E.payroll(S, c), n: E.squadOf(S, c).length }))
    .sort((a, b) => b.pay - a.pay);
  const maxPay = Math.max(S.cap, ...leagueRows.map((r) => r.pay)) * 1.04;

  // ---------- shared row styles ----------
  const rowSx = {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto auto',
    gap: '4px 10px',
    alignItems: 'center',
    px: 2,
    py: 1,
    borderBottom: `1px solid ${colors.line}`,
    '&:last-child': { borderBottom: 0 },
  } as const;
  const swatch = (color: string, extra: object = {}) => (
    <Box sx={{ width: 8, height: 28, borderRadius: '2px', background: color, ...extra }} />
  );
  const panelHead = (title: string, note?: React.ReactNode, tools?: React.ReactNode) => (
    <Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: `1px solid ${colors.line}` }}>
      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {note && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {note}
        </Typography>
      )}
      {tools}
    </Box>
  );
  const chipBtn = (label: React.ReactNode, onClick: () => void, extra: object = {}, title?: string) => (
    <Tooltip title={title || ''} disableHoverListener={!title}>
      <Button
        size="small"
        variant="outlined"
        onClick={onClick}
        sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 13, textTransform: 'none', whiteSpace: 'nowrap', ...extra }}
      >
        {label}
      </Button>
    </Tooltip>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* ---------- header ---------- */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '14px 24px', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              Salary Playground
            </Typography>
            <Chip label={`${data.league} ${data.season}`} color="secondary" size="small" sx={{ fontWeight: 700 }} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Release, sign, trade and reprice players. Every move updates both clubs.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.25} flexWrap="wrap" alignItems="center" useFlexGap>
          <TextField label="Cap (Cr)" type="number" size="small" value={S.cap} inputProps={{ min: 1, step: 0.5 }} onChange={(e) => setSetting('cap', e.target.value)} sx={{ width: 110 }} />
          <TextField label="Max squad" type="number" size="small" value={S.maxSquad} inputProps={{ min: 1, step: 1 }} onChange={(e) => setSetting('maxSquad', e.target.value)} sx={{ width: 110 }} />
          <TextField label="Max overseas" type="number" size="small" value={S.maxOS} inputProps={{ min: 0, step: 1 }} onChange={(e) => setSetting('maxOS', e.target.value)} sx={{ width: 120 }} />
          <Tooltip title="Reload salaries from BigQuery (bypasses the cache)">
            <span>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                disabled={refreshing}
                onClick={() => loadData(true)}
                startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                sx={{ textTransform: 'none' }}
              >
                {refreshing ? 'Refreshing…' : 'Refresh salaries'}
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" size="small" onClick={resetTeam} sx={{ textTransform: 'none' }}>
            Reset this team
          </Button>
          <Button variant="outlined" size="small" color="inherit" onClick={resetAll} sx={{ textTransform: 'none' }}>
            Reset everything
          </Button>
        </Stack>
      </Box>

      {/* ---------- team strip ---------- */}
      <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.5, mb: 2 }}>
        {ref.codes.map((c) => {
          const p = E.payroll(S, c);
          const active = c === t;
          return (
            <Box
              key={c}
              component="button"
              onClick={() => openTeam(c)}
              title={ref.teams[c].name}
              sx={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                minWidth: 96,
                px: 1.25,
                py: 1,
                border: `1px solid ${active ? colors.ink : colors.line}`,
                boxShadow: active ? `inset 0 0 0 1px ${colors.ink}` : 'none',
                borderRadius: '10px',
                background: colors.panel,
                '&:hover': { borderColor: colors.ink },
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <TeamLogo short={c} size={20} />
                <Typography component="span" sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1 }}>
                  {c}
                </Typography>
                {E.teamEdited(S, c) && (
                  <Tooltip title="Edited">
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: colors.warn }} />
                  </Tooltip>
                )}
              </Stack>
              <Typography component="span" sx={{ fontSize: 12.5, color: p > S.cap ? colors.over : colors.muted, fontWeight: p > S.cap ? 600 : 400 }}>
                {cr(p)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ---------- purse board ---------- */}
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.75 }, pb: 2.25, mb: 2, borderRadius: '12px' }} aria-live="polite">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px 28px' }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
              <TeamLogo short={t} size={26} />
              <Typography variant="body2" color="text.secondary">
                {tm.name}, remaining purse
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 'clamp(46px, 9.5vw, 92px)',
                lineHeight: 0.88,
                letterSpacing: '-0.5px',
                fontVariantNumeric: 'tabular-nums',
                color: rem < 0 ? colors.over : rem < 3 ? colors.warn : colors.ink,
              }}
            >
              ₹{fmt(Math.abs(rem))}
              <Typography component="small" sx={{ fontSize: '0.36em', fontWeight: 600, ml: 0.75, color: colors.muted }}>
                {rem < 0 ? 'Cr over the cap' : 'Cr left'}
              </Typography>
            </Typography>
          </Box>
          <Stack direction="row" spacing={3.5} flexWrap="wrap" useFlexGap>
            {[
              { v: `₹${fmt(pay)} / ${fmt(S.cap)}`, k: 'committed', bad: rem < 0 },
              { v: `${squad.length} / ${S.maxSquad}`, k: 'squad size', bad: squad.length > S.maxSquad },
              { v: `${osN} / ${S.maxOS}`, k: 'overseas', bad: osN > S.maxOS },
            ].map((f) => (
              <Box key={f.k}>
                <Typography sx={{ fontWeight: 600, fontSize: 26, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: f.bad ? colors.over : colors.ink }}>
                  {f.v}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {f.k}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* contract bar */}
        <Box
          sx={{
            position: 'relative',
            height: 38,
            mt: 2.5,
            borderRadius: '6px',
            backgroundColor: colors.paper,
            backgroundImage: `repeating-linear-gradient(90deg, transparent 0 9px, ${colors.line} 9px 10px)`,
          }}
        >
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', borderRadius: '6px', overflow: 'hidden' }}>
            {cd > 0 && (
              <Box
                tabIndex={0}
                aria-label={`Cap differential ${cr(cd)}`}
                onMouseOver={() => setHoverInfo(`Cap differential: ${cr(cd)}`)}
                onFocus={() => setHoverInfo(`Cap differential: ${cr(cd)}`)}
                onClick={() => setHoverInfo(`Cap differential: ${cr(cd)}`)}
                sx={{
                  height: '100%',
                  flex: '0 0 auto',
                  width: `${((cd / scale) * 100).toFixed(3)}%`,
                  borderRight: `1px solid ${colors.panel}`,
                  background: `repeating-linear-gradient(135deg, ${colors.warn} 0 4px, ${colors.warnSoft} 4px 8px)`,
                }}
              />
            )}
            {(() => {
              let run = cd;
              return squad.map((p, i) => {
                const v = E.curPrice(S, p);
                const start = run;
                run += v;
                const beyond = start >= S.cap - 1e-9;
                const isNew = E.origTeam(p) !== t;
                const bg = beyond ? colors.over : isNew ? colors.turf : i % 2 ? colors.segAlt : colors.seg;
                const label = `${p.name}: ${cr(v)}`;
                return (
                  <Box
                    key={p.id}
                    tabIndex={0}
                    aria-label={`${p.name} ${cr(v)}`}
                    onMouseOver={() => setHoverInfo(label)}
                    onFocus={() => setHoverInfo(label)}
                    onClick={() => setHoverInfo(label)}
                    sx={{
                      height: '100%',
                      flex: '0 0 auto',
                      width: `${((v / scale) * 100).toFixed(3)}%`,
                      borderRight: `1px solid ${colors.panel}`,
                      background: bg,
                      opacity: isNew && !beyond && i % 2 ? 0.8 : 1,
                      transition: 'width .25s ease',
                      cursor: 'default',
                    }}
                  />
                );
              });
            })()}
          </Box>
          <Box sx={{ position: 'absolute', top: -8, bottom: -8, width: 2, background: colors.ink, left: `calc(${((S.cap / scale) * 100).toFixed(3)}% - 1px)` }}>
            <Typography component="span" sx={{ position: 'absolute', top: -20, transform: 'translateX(-50%)', fontSize: 12, whiteSpace: 'nowrap', fontWeight: 600, color: colors.ink }}>
              Cap {cr(S.cap)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25, flexWrap: 'wrap', mt: 1.25 }}>
          <Typography variant="body2" sx={{ minHeight: 20 }}>
            {hoverInfo}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pay > 0 ? `Top 3 contracts take ${Math.round((top3 / pay) * 100)}% of payroll` : ''}
          </Typography>
        </Box>
      </Paper>

      {/* ---------- squad + pool ---------- */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.25, alignItems: 'start' }}>
        {/* squad */}
        <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          {panelHead(
            `${t} squad`,
            <>
              Tap a price to change it. Overseas is anyone whose nationality isn't India. * nationality not checked on Wikipedia.
            </>
          )}
          <Box sx={{ maxHeight: 560, overflowY: 'auto' }}>
            {ri.capped.length > 0 && (
              <Box sx={{ ...rowSx, background: colors.warnSoft }}>
                {swatch('transparent', { backgroundImage: `repeating-linear-gradient(135deg, ${colors.warn} 0 3px, transparent 3px 6px)` })}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>Cap differential</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {ri.capped.length} capped retention{ri.capped.length > 1 ? 's' : ''} remaining, paid ₹{fmt(ri.actual)}. Minimum guideline for {ri.capped.length}: ₹{fmt(ri.guide)}. Charged the higher, so{' '}
                    {ri.diff > 0 ? `₹${fmt(ri.diff)} is added to the cap` : 'nothing extra is added'}.
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: 20, textAlign: 'right', minWidth: 62, fontVariantNumeric: 'tabular-nums' }}>{fmt(ri.diff)}</Typography>
                <Box />
              </Box>
            )}
            {squad.length ? (
              squad.map((p) => {
                const ot = E.origTeam(p);
                const v = E.curPrice(S, p);
                const meta: React.ReactNode[] = [];
                const tr = !p.custom ? ref.traded[p.name] : undefined;
                if (tr && ot === t && tr.to === t) {
                  meta.push(
                    <span key="tr">
                      <Box component="span" sx={{ color: colors.turf, fontWeight: 600 }}>Traded from {tr.from}, {tr.when}</Box>
                      {tr.was !== null ? ` fee revised from ₹${fmt(tr.was)}` : ''}
                    </span>
                  );
                }
                if (p.custom) meta.push(<Box key="new" component="span" sx={{ color: colors.turf, fontWeight: 600 }}>New signing</Box>);
                else if (ot !== t) meta.push(<Box key="from" component="span" sx={{ color: colors.turf, fontWeight: 600 }}>From {ot}</Box>);
                if (!p.custom && S.price[p.id] !== undefined) {
                  meta.push(<Box key="rp" component="span" sx={{ color: colors.warn, fontWeight: 600 }}>Repriced from ₹{fmt(p.price)}</Box>);
                }
                const r = E.retOf(p);
                if (E.isRetained(S, p) && r && r[0] === t) {
                  if (r[1] === 'C') {
                    const f = E.freedBy(S, p, t);
                    meta.push(
                      <span key="rt">
                        <Box component="span" sx={{ color: colors.ink, fontWeight: 600 }}>Capped retention</Box> releasing frees ₹{fmt(f)}
                        {Math.abs(f - v) > 0.001 ? ` of ₹${fmt(v)}` : ''}
                      </span>
                    );
                  } else {
                    meta.push(<Box key="rt" component="span" sx={{ color: colors.ink, fontWeight: 600 }}>Uncapped retention</Box>);
                  }
                }
                const os = E.isOS(S, p);
                return (
                  <Box key={p.id} sx={rowSx}>
                    {swatch(p.custom ? colors.turf : ref.teams[ot as string]?.color || colors.line)}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <Box component="span" sx={{ color: os ? colors.ink : colors.muted, fontWeight: os ? 600 : 400 }}>{E.natLabel(S, p)}</Box>
                        {meta.map((m, i) => (
                          <React.Fragment key={i}> · {m}</React.Fragment>
                        ))}
                      </Typography>
                    </Box>
                    <Box sx={{ fontWeight: 600, fontSize: 20, textAlign: 'right', minWidth: 62, fontVariantNumeric: 'tabular-nums' }}>
                      {editingId === p.id ? (
                        <TextField
                          autoFocus
                          type="number"
                          size="small"
                          value={editValue}
                          inputProps={{ min: 0, step: 0.05, 'aria-label': `New price for ${p.name}`, style: { textAlign: 'right', fontWeight: 600, fontSize: 18, padding: '3px 6px', width: 70 } }}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit(true);
                            if (e.key === 'Escape') commitEdit(false);
                          }}
                          onBlur={() => commitEdit(true)}
                          onFocus={(e) => e.target.select()}
                        />
                      ) : (
                        <Box
                          component="button"
                          onClick={() => startEdit(p)}
                          aria-label={`Change price for ${p.name}`}
                          sx={{ border: 0, font: 'inherit', p: 0, background: 'none', cursor: 'pointer', color: 'inherit' }}
                        >
                          {fmt(v)}
                        </Box>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.75}>
                      {p.custom &&
                        chipBtn(
                          <FlightTakeoff sx={{ fontSize: 16, display: 'block' }} aria-label="Overseas" />,
                          () => setS(E.toggleOS(S, p.id)),
                          S.os[p.id]
                            ? { background: colors.ink, color: colors.panel, borderColor: colors.ink, '&:hover': { background: colors.ink, borderColor: colors.ink } }
                            : { color: colors.ink, borderColor: colors.line },
                          'Mark this signing as overseas'
                        )}
                      {chipBtn(p.custom ? 'Remove' : 'Release', () => release(p), { color: colors.ink, borderColor: colors.line })}
                    </Stack>
                  </Box>
                );
              })
            ) : (
              <Typography sx={{ p: 3.5, px: 2 }} color="text.secondary">
                No players yet. Add players from the pool or sign one below.
              </Typography>
            )}
          </Box>
          <Box component="form" onSubmit={signCustom} autoComplete="off" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1.5, px: 2, borderTop: `1px solid ${colors.line}`, background: colors.paper }}>
            <TextField size="small" placeholder="Auction target or new signing" value={customName} onChange={(e) => setCustomName(e.target.value)} required inputProps={{ 'aria-label': 'Player name' }} sx={{ flex: '1 1 150px' }} />
            <TextField size="small" type="number" placeholder="Price Cr" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)} required inputProps={{ min: 0, step: 0.05, 'aria-label': 'Price in crore' }} sx={{ width: 110 }} />
            <Button type="submit" variant="contained" size="small" sx={{ textTransform: 'none' }}>
              Sign player
            </Button>
          </Box>
        </Paper>

        {/* pool */}
        <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          {panelHead(
            'Player pool',
            'Players at other clubs, plus anyone released. Adding a player takes him from his current club.',
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
              <TextField size="small" type="search" placeholder="Search players" value={q} onChange={(e) => setQ(e.target.value)} inputProps={{ 'aria-label': 'Search players' }} sx={{ flex: '1 1 160px' }} />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Club</InputLabel>
                <Select label="Club" value={fTeam} onChange={(e) => setFTeam(e.target.value)}>
                  <MenuItem value="all">All clubs</MenuItem>
                  <MenuItem value={POOL}>Released and unsigned</MenuItem>
                  {ref.codes.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Fit</InputLabel>
                <Select label="Fit" value={fFit} onChange={(e) => setFFit(e.target.value as 'all' | 'fit')}>
                  <MenuItem value="all">Any price</MenuItem>
                  <MenuItem value="fit">Fits remaining cap</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Nationality</InputLabel>
                <Select label="Nationality" value={fNat} onChange={(e) => setFNat(e.target.value as 'all' | 'in' | 'os')}>
                  <MenuItem value="all">Any nationality</MenuItem>
                  <MenuItem value="in">Indian</MenuItem>
                  <MenuItem value="os">Overseas</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort</InputLabel>
                <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value as 'pd' | 'pa' | 'n')}>
                  <MenuItem value="pd">Price high to low</MenuItem>
                  <MenuItem value="pa">Price low to high</MenuItem>
                  <MenuItem value="n">Name</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
          <Box sx={{ maxHeight: 560, overflowY: 'auto' }}>
            {pool.length ? (
              pool.map((p) => {
                const ct = E.curTeam(S, p);
                const v = E.curPrice(S, p);
                const fits = v <= rem + 1e-9;
                const ot = E.origTeam(p);
                let where = ct === POOL ? (p.custom ? 'Unsigned' : `Released by ${ot}`) : ref.teams[ct].name + (ot && ot !== ct ? ` (was ${ot})` : '');
                const r = E.retOf(p);
                if (ct !== POOL && E.isRetained(S, p) && r && r[1] === 'C') {
                  where += `. Capped retention: trading him frees ₹${fmt(E.freedBy(S, p, ct))} for ${ct}`;
                }
                const os = E.isOS(S, p);
                return (
                  <Box key={p.id} sx={rowSx}>
                    {swatch(ct === POOL ? colors.line : ref.teams[ct].color)}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <Box component="span" sx={{ color: os ? colors.ink : colors.muted, fontWeight: os ? 600 : 400 }}>{E.natLabel(S, p)}</Box> · {where}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 20, textAlign: 'right', minWidth: 62, fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</Typography>
                    {chipBtn(
                      fits ? 'Add' : `Add (+${fmt(v - rem)} over)`,
                      () => add(p),
                      fits ? { color: colors.turf, borderColor: colors.turf, fontWeight: 600 } : { color: colors.muted, borderColor: colors.line },
                      fits ? 'Fits under the cap' : `Needs ₹${fmt(v - rem)} Cr more room`
                    )}
                  </Box>
                );
              })
            ) : (
              <Typography sx={{ p: 3.5, px: 2 }} color="text.secondary">
                No players match. Clear the search or change the filters.
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {/* ---------- trades ---------- */}
      <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden', mt: 2.25 }}>
        {panelHead(
          'Trade players',
          'Pick players from each club and set the fee each one joins at. Fees start at the current salary. Leave one side empty for a straight transfer.'
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, borderBottom: `1px solid ${colors.line}` }}>
          {(['a', 'b'] as const).map((side, idx) => (
            <Box
              key={side}
              sx={{
                p: 1.5,
                px: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                minWidth: 0,
                borderLeft: { md: idx ? `1px solid ${colors.line}` : 0 },
                borderTop: { xs: idx ? `1px solid ${colors.line}` : 0, md: 0 },
              }}
            >
              <FormControl size="small">
                <InputLabel>{idx ? 'Second club' : 'First club'}</InputLabel>
                <Select label={idx ? 'Second club' : 'First club'} value={T[side]} onChange={(e) => setT((prev) => ({ ...prev, [side]: e.target.value }))}>
                  {ref.codes.map((c) => (
                    <MenuItem key={c} value={c}>
                      {ref.teams[c].name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" type="search" placeholder="Search this club" value={side === 'a' ? qa : qb} onChange={(e) => (side === 'a' ? setQa(e.target.value) : setQb(e.target.value))} />
              <Box sx={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${colors.line}`, borderRadius: '8px' }}>
                {tradeList(side).length ? (
                  tradeList(side).map((p) => {
                    const on = T.sel[p.id] !== undefined;
                    const r = E.retOf(p);
                    const cappedRet = E.isRetained(S, p) && r && r[0] === T[side] && r[1] === 'C';
                    return (
                      <Box
                        key={p.id}
                        component="label"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 0.5, borderBottom: `1px solid ${colors.line}`, cursor: 'pointer', fontSize: 14, background: on ? colors.turfSoft : 'transparent', '&:last-child': { borderBottom: 0 } }}
                      >
                        <Checkbox size="small" checked={on} onChange={(e) => toggleTradeSel(p, e.target.checked)} sx={{ p: 0.25 }} color="success" />
                        <Box sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.name}
                          <Typography component="small" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                            {E.natLabel(S, p)}
                            {cappedRet ? ', capped retention' : ''}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>{fmt(E.curPrice(S, p))}</Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Typography sx={{ p: 1.75 }} color="text.secondary" variant="body2">
                    No players match.
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ p: 1.5, px: 2 }}>
          {items.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Player</TableCell>
                  <TableCell>Move</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">Joins at (Cr)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((x) => (
                  <TableRow key={x.id}>
                    <TableCell>{x.p.name}</TableCell>
                    <TableCell>
                      {x.from} to {x.to}
                    </TableCell>
                    <TableCell align="right">{fmt(E.curPrice(S, x.p))}</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={x.fee}
                        inputProps={{ min: 0, step: 0.05, style: { textAlign: 'right', fontWeight: 600, fontSize: 17, padding: '3px 6px', width: 80 }, 'aria-label': `Fee for ${x.p.name}` }}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!isNaN(v) && v >= 0) setT((prev) => ({ ...prev, sel: { ...prev.sel, [x.id]: round2(v) } }));
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
              Tick players on either side to build a trade.
            </Typography>
          )}
        </Box>
        {sim && (
          <Box sx={{ px: 2, pb: 1.5, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Club</TableCell>
                  <TableCell align="right">Payroll now</TableCell>
                  <TableCell align="right">After trade</TableCell>
                  <TableCell align="right">Change</TableCell>
                  <TableCell align="right">Left under cap</TableCell>
                  <TableCell align="right">Cap differential</TableCell>
                  <TableCell align="right">Squad</TableCell>
                  <TableCell align="right">Overseas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[T.a, T.b].map((c) => {
                  const b = sim.before[c];
                  const a = sim.after[c];
                  const d = round2(a.pay - b.pay);
                  const left = round2(S.cap - a.pay);
                  const num = { fontSize: 17, fontWeight: 600, fontVariantNumeric: 'tabular-nums' } as const;
                  return (
                    <TableRow key={c}>
                      <TableCell sx={{ fontWeight: 700 }}>{c}</TableCell>
                      <TableCell align="right" sx={num}>{fmt(b.pay)}</TableCell>
                      <TableCell align="right" sx={num}>{fmt(a.pay)}</TableCell>
                      <TableCell align="right" sx={{ ...num, color: d > 0 ? colors.over : d < 0 ? colors.turf : 'inherit' }}>
                        {d > 0 ? '+' : d < 0 ? '−' : ''}{fmt(Math.abs(d))}
                      </TableCell>
                      <TableCell align="right" sx={{ ...num, color: left < 0 ? colors.over : 'inherit' }}>
                        {left < 0 ? '−' : ''}{fmt(Math.abs(left))}
                      </TableCell>
                      <TableCell align="right">{fmt(b.diff)} to {fmt(a.diff)}</TableCell>
                      <TableCell align="right">{a.n}</TableCell>
                      <TableCell align="right">{a.os}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 2, pb: 1.75 }}>
          <Button variant="contained" size="small" disabled={!items.length || T.a === T.b} onClick={completeTrade} sx={{ textTransform: 'none' }}>
            Complete trade
          </Button>
          <Button variant="outlined" size="small" color="inherit" onClick={() => setT((prev) => ({ ...prev, sel: {} }))} sx={{ textTransform: 'none' }}>
            Clear
          </Button>
          {tradeWarn && (
            <Typography variant="body2" sx={{ color: colors.over }}>
              {tradeWarn}
            </Typography>
          )}
        </Stack>
        {S.trades.length > 0 && (
          <Box sx={{ px: 2, pb: 1.75 }}>
            {S.trades.map((tr, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25, py: 0.75, borderTop: `1px dashed ${colors.line}`, fontSize: 14 }}>
                <span>{tr.label}</span>
                {i === S.trades.length - 1 && chipBtn('Undo', undoTrade, { color: colors.ink, borderColor: colors.line })}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* ---------- moves + league ---------- */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.25, mt: 2.25, alignItems: 'start' }}>
        <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          {panelHead('Moves for this team', `Compared with the ${data.season} salaries. Cap differential follows the retention release guideline.`)}
          <Box sx={{ px: 2, pt: 0.75, pb: 1.5 }}>
            {mv.moves.length ? (
              mv.moves.map((m, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25, py: 0.875, borderBottom: `1px dashed ${colors.line}`, fontSize: 14.5, '&:last-child': { borderBottom: 0 } }}>
                  <span>{m.text}</span>
                  <Box component="span" sx={{ fontWeight: 600, fontSize: 17, whiteSpace: 'nowrap', color: m.delta > 0 ? colors.over : colors.turf }}>
                    {m.delta > 0 ? '+' : '−'}₹{fmt(Math.abs(m.delta))}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1.75 }}>
                No changes yet. This is the {data.season} {t} payroll from the salary table.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.25, borderTop: `1px solid ${colors.line}`, fontWeight: 600 }}>
            <span>
              Payroll ₹{fmt(mv.orig)} to ₹{fmt(mv.pay)} Cr
            </span>
            <Box component="span" sx={{ fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>
              {mv.net > 0 ? '+' : mv.net < 0 ? '−' : ''}₹{fmt(Math.abs(mv.net))}
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          {panelHead('League payrolls', 'Current scenario for all ten clubs. The dark line is the cap. Select a club to open it.')}
          <Box sx={{ px: 2, pt: 1.25, pb: 1.75, overflowX: 'auto' }}>
            {leagueRows.map((r) => (
              <Box
                key={r.c}
                role="button"
                tabIndex={0}
                onClick={() => {
                  openTeam(r.c);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openTeam(r.c);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                sx={{ display: 'grid', gridTemplateColumns: '48px 1fr 70px 44px', gap: 1, alignItems: 'center', py: 0.625, fontSize: 14, cursor: 'pointer', borderRadius: '4px', '&:hover': { background: colors.paper } }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 17 }}>{r.c}</Typography>
                <Box sx={{ position: 'relative', height: 14, background: colors.paper, borderRadius: '3px' }}>
                  <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: '3px', background: ref.teams[r.c].color, width: `${((r.pay / maxPay) * 100).toFixed(2)}%` }} />
                  <Box sx={{ position: 'absolute', top: -3, bottom: -3, width: 2, background: colors.ink, left: `${((S.cap / maxPay) * 100).toFixed(2)}%` }} />
                </Box>
                <Typography sx={{ textAlign: 'right', fontWeight: 600, fontSize: 17, fontVariantNumeric: 'tabular-nums', color: r.pay > S.cap ? colors.over : 'inherit' }}>{fmt(r.pay)}</Typography>
                <Typography sx={{ textAlign: 'right', fontSize: 13, color: r.n > S.maxSquad ? colors.over : colors.muted }}>{r.n}p</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={1800}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default SalaryPlayground;
